import type { RouteCard } from '../data/routeCards'
import type { ModelTier } from '../domain/types'

export const MODEL_TIERS = [
  'direct',
  'standard',
  'deep',
] as const satisfies readonly ModelTier[]

export type SemanticRankingEntry = {
  id: string
  similarity: number
}

export type RoutedCardMatch = {
  routeCardId: string
  similarity: number
  tier: ModelTier
}

export type TierEvidence = {
  score: number
  routeCardIds: string[]
  pieceIndexes: number[]
}

export type TierScoreSet = Record<
  ModelTier,
  TierEvidence | null
>

export type SemanticRoutingThresholds = {
  minSimilarity: number
  minTierMargin: number
}

export const FROZEN_SEMANTIC_MIXTURE_POLICY = {
  method: 'mixture',
  wholeWeight: 0.5,
  thresholds: {
    minSimilarity: 0.6,
    minTierMargin: 0.04,
  },
} as const

export type SemanticRouteDecision = {
  tier?: ModelTier
  abstained: boolean
  abstentionReason?:
    | 'incomplete-tier-corpus'
    | 'below-similarity-floor'
    | 'below-tier-margin'
    | 'exact-tie'
  tierScores: TierScoreSet
  winnerScore?: number
  runnerUpScore?: number
  margin?: number
}

export type SemanticRouteEvaluationCase = {
  expectedTier: ModelTier
  predictedTier?: ModelTier
}

export type SemanticRouteMetrics = {
  caseCount: number
  routedCount: number
  abstainedCount: number
  coverage: number
  abstentionRate: number
  exactCount: number
  exactRateOverall: number
  exactRateRouted: number
  falseDowngradeCount: number
  falseDowngradeRateRouted: number
  downgradeSeveritySteps: number
  overrouteCount: number
  excessTierSteps: number
  meanExcessTierRouted: number
}

export type FallbackRouteEvaluationCase = {
  expectedTier: ModelTier
  semanticTier?: ModelTier
  fallbackTier: ModelTier
}

export type FallbackRouteMetrics = {
  caseCount: number
  semanticRoutedCount: number
  semanticCoverage: number
  fallbackUsedCount: number
  semanticOverrideCount: number
  exactCount: number
  exactRate: number
  falseDowngradeCount: number
  falseDowngradeRate: number
  downgradeSeveritySteps: number
  overrouteCount: number
  excessTierSteps: number
  meanExcessTier: number
}

const TIER_RANK: Record<ModelTier, number> = {
  direct: 0,
  standard: 1,
  deep: 2,
}

function emptyTierScores(): TierScoreSet {
  return {
    direct: null,
    standard: null,
    deep: null,
  }
}

function safeRate(
  numerator: number,
  denominator: number,
): number {
  return denominator === 0 ? 0 : numerator / denominator
}

function normalizedPiece(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function compareEvidence(
  left: TierEvidence,
  right: TierEvidence,
): number {
  if (left.score !== right.score) {
    return left.score - right.score
  }
  const leftPiece = left.pieceIndexes[0] ?? -1
  const rightPiece = right.pieceIndexes[0] ?? -1
  if (leftPiece !== rightPiece) {
    return rightPiece - leftPiece
  }
  return (right.routeCardIds[0] ?? '').localeCompare(
    left.routeCardIds[0] ?? '',
  )
}

function uniqueValues<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function assertThresholds(
  thresholds: SemanticRoutingThresholds,
) {
  if (
    !Number.isFinite(thresholds.minSimilarity) ||
    !Number.isFinite(thresholds.minTierMargin) ||
    thresholds.minTierMargin < 0
  ) {
    throw new Error(
      'Semantic routing thresholds must be finite, and the tier margin must be non-negative.',
    )
  }
}

export function isSemanticRoutingCard(
  card: RouteCard,
): boolean {
  return (
    card.temporalScope === 'current' &&
    card.mapping.tier !== undefined
  )
}

export function splitPromptForSemanticRouting(
  prompt: string,
  maxPieces = 12,
): string[] {
  if (!Number.isInteger(maxPieces) || maxPieces < 1) {
    throw new Error('maxPieces must be a positive integer.')
  }

  const pieces: string[] = []
  const seen = new Set<string>()
  const segmenter = new Intl.Segmenter('en', {
    granularity: 'sentence',
  })

  for (const rawLine of prompt.replace(/\r\n?/g, '\n').split('\n')) {
    const line = normalizedPiece(
      rawLine.replace(
        /^\s*(?:[-*+•]|\d+[.)])\s+/,
        '',
      ),
    )
    if (!line) continue

    for (const sentence of segmenter.segment(line)) {
      for (const clause of sentence.segment.split(';')) {
        const piece = normalizedPiece(clause)
        if (!piece || seen.has(piece)) continue
        seen.add(piece)
        pieces.push(piece)
      }
    }
  }

  if (pieces.length <= maxPieces) {
    return pieces
  }
  if (maxPieces === 1) {
    return [normalizedPiece(pieces.join(' '))]
  }

  return [
    ...pieces.slice(0, maxPieces - 1),
    normalizedPiece(
      pieces.slice(maxPieces - 1).join(' '),
    ),
  ]
}

export function filterSemanticRoutingRanking(
  ranking: SemanticRankingEntry[],
  cardsById: ReadonlyMap<string, RouteCard>,
  excludedCardIds: ReadonlySet<string> = new Set(),
): RoutedCardMatch[] {
  return ranking.flatMap(({ id, similarity }) => {
    const card = cardsById.get(id)
    if (
      !card ||
      excludedCardIds.has(id) ||
      !isSemanticRoutingCard(card) ||
      !Number.isFinite(similarity)
    ) {
      return []
    }

    return [
      {
        routeCardId: id,
        similarity,
        tier: card.mapping.tier!,
      },
    ]
  })
}

export function tierScoresFromRanking(
  ranking: RoutedCardMatch[],
  pieceIndex?: number,
): TierScoreSet {
  const scores = emptyTierScores()

  for (const match of ranking) {
    const candidate: TierEvidence = {
      score: match.similarity,
      routeCardIds: [match.routeCardId],
      pieceIndexes:
        pieceIndex === undefined ? [] : [pieceIndex],
    }
    const current = scores[match.tier]
    if (
      current === null ||
      compareEvidence(candidate, current) > 0
    ) {
      scores[match.tier] = candidate
    }
  }

  return scores
}

export function aggregatePieceTierScores(
  pieceScores: TierScoreSet[],
): TierScoreSet {
  const aggregate = emptyTierScores()

  for (const scores of pieceScores) {
    for (const tier of MODEL_TIERS) {
      const candidate = scores[tier]
      const current = aggregate[tier]
      if (
        candidate !== null &&
        (current === null ||
          compareEvidence(candidate, current) > 0)
      ) {
        aggregate[tier] = {
          score: candidate.score,
          routeCardIds: [...candidate.routeCardIds],
          pieceIndexes: [...candidate.pieceIndexes],
        }
      }
    }
  }

  return aggregate
}

export function mixTierScores(
  whole: TierScoreSet,
  piecewise: TierScoreSet,
  wholeWeight = 0.5,
): TierScoreSet {
  if (
    !Number.isFinite(wholeWeight) ||
    wholeWeight < 0 ||
    wholeWeight > 1
  ) {
    throw new Error(
      'wholeWeight must be a finite value from 0 to 1.',
    )
  }

  const mixed = emptyTierScores()
  for (const tier of MODEL_TIERS) {
    const wholeEvidence = whole[tier]
    const pieceEvidence = piecewise[tier]
    if (
      wholeEvidence === null ||
      pieceEvidence === null
    ) {
      continue
    }

    mixed[tier] = {
      score:
        wholeEvidence.score * wholeWeight +
        pieceEvidence.score * (1 - wholeWeight),
      routeCardIds: uniqueValues([
        ...wholeEvidence.routeCardIds,
        ...pieceEvidence.routeCardIds,
      ]),
      pieceIndexes: uniqueValues([
        ...wholeEvidence.pieceIndexes,
        ...pieceEvidence.pieceIndexes,
      ]).sort((left, right) => left - right),
    }
  }

  return mixed
}

export function decideSemanticRoute(
  tierScores: TierScoreSet,
  thresholds: SemanticRoutingThresholds,
): SemanticRouteDecision {
  assertThresholds(thresholds)

  if (
    MODEL_TIERS.some((tier) => {
      const evidence = tierScores[tier]
      return (
        evidence === null ||
        !Number.isFinite(evidence.score)
      )
    })
  ) {
    return {
      abstained: true,
      abstentionReason: 'incomplete-tier-corpus',
      tierScores,
    }
  }

  const ranked = MODEL_TIERS.map((tier) => ({
    tier,
    evidence: tierScores[tier]!,
  })).sort(
    (left, right) =>
      right.evidence.score - left.evidence.score ||
      TIER_RANK[left.tier] - TIER_RANK[right.tier],
  )
  const winner = ranked[0]!
  const runnerUp = ranked[1]!
  const margin =
    winner.evidence.score - runnerUp.evidence.score
  const comparisonTolerance =
    Number.EPSILON *
    Math.max(
      1,
      Math.abs(winner.evidence.score),
      Math.abs(runnerUp.evidence.score),
      Math.abs(thresholds.minTierMargin),
    ) *
    4
  const shared = {
    tierScores,
    winnerScore: winner.evidence.score,
    runnerUpScore: runnerUp.evidence.score,
    margin,
  }

  if (margin === 0) {
    return {
      ...shared,
      abstained: true,
      abstentionReason: 'exact-tie',
    }
  }
  if (
    winner.evidence.score < thresholds.minSimilarity
  ) {
    return {
      ...shared,
      abstained: true,
      abstentionReason: 'below-similarity-floor',
    }
  }
  if (
    margin + comparisonTolerance <
    thresholds.minTierMargin
  ) {
    return {
      ...shared,
      abstained: true,
      abstentionReason: 'below-tier-margin',
    }
  }

  return {
    ...shared,
    tier: winner.tier,
    abstained: false,
  }
}

export function basisCardMask(evalCase: {
  basisRouteCardIds: readonly string[]
}): Set<string> {
  return new Set(evalCase.basisRouteCardIds)
}

export function basisSourceFamilyMask(
  evalCase: {
    basisRouteCardIds: readonly string[]
  },
  cards: readonly RouteCard[],
): Set<string> {
  const basisIds = basisCardMask(evalCase)
  const sourceUrls = new Set(
    cards
      .filter((card) => basisIds.has(card.id))
      .map((card) => card.sourceUrl),
  )
  const result = new Set(basisIds)

  for (const card of cards) {
    if (sourceUrls.has(card.sourceUrl)) {
      result.add(card.id)
    }
  }

  return result
}

export function summarizeSemanticRouteResults(
  cases: readonly SemanticRouteEvaluationCase[],
): SemanticRouteMetrics {
  let routedCount = 0
  let exactCount = 0
  let falseDowngradeCount = 0
  let downgradeSeveritySteps = 0
  let overrouteCount = 0
  let excessTierSteps = 0

  for (const evalCase of cases) {
    if (evalCase.predictedTier === undefined) continue
    routedCount += 1
    if (evalCase.predictedTier === evalCase.expectedTier) {
      exactCount += 1
    }

    const delta =
      TIER_RANK[evalCase.predictedTier] -
      TIER_RANK[evalCase.expectedTier]
    if (delta < 0) {
      falseDowngradeCount += 1
      downgradeSeveritySteps += -delta
    } else if (delta > 0) {
      overrouteCount += 1
      excessTierSteps += delta
    }
  }

  const caseCount = cases.length
  const abstainedCount = caseCount - routedCount
  return {
    caseCount,
    routedCount,
    abstainedCount,
    coverage: safeRate(routedCount, caseCount),
    abstentionRate: safeRate(abstainedCount, caseCount),
    exactCount,
    exactRateOverall: safeRate(exactCount, caseCount),
    exactRateRouted: safeRate(exactCount, routedCount),
    falseDowngradeCount,
    falseDowngradeRateRouted: safeRate(
      falseDowngradeCount,
      routedCount,
    ),
    downgradeSeveritySteps,
    overrouteCount,
    excessTierSteps,
    meanExcessTierRouted: safeRate(
      excessTierSteps,
      routedCount,
    ),
  }
}

export function resolveSemanticRoute(
  semanticTier: ModelTier | undefined,
  fallbackTier: ModelTier,
): {
  tier: ModelTier
  usedFallback: boolean
} {
  return semanticTier === undefined
    ? {
        tier: fallbackTier,
        usedFallback: true,
      }
    : {
        tier: semanticTier,
        usedFallback: false,
      }
}

export function summarizeFallbackRouteResults(
  cases: readonly FallbackRouteEvaluationCase[],
): FallbackRouteMetrics {
  let semanticRoutedCount = 0
  let fallbackUsedCount = 0
  let semanticOverrideCount = 0
  let exactCount = 0
  let falseDowngradeCount = 0
  let downgradeSeveritySteps = 0
  let overrouteCount = 0
  let excessTierSteps = 0

  for (const evalCase of cases) {
    const resolved = resolveSemanticRoute(
      evalCase.semanticTier,
      evalCase.fallbackTier,
    )
    if (resolved.usedFallback) {
      fallbackUsedCount += 1
    } else {
      semanticRoutedCount += 1
      if (evalCase.semanticTier !== evalCase.fallbackTier) {
        semanticOverrideCount += 1
      }
    }
    if (resolved.tier === evalCase.expectedTier) {
      exactCount += 1
    }

    const delta =
      TIER_RANK[resolved.tier] -
      TIER_RANK[evalCase.expectedTier]
    if (delta < 0) {
      falseDowngradeCount += 1
      downgradeSeveritySteps += -delta
    } else if (delta > 0) {
      overrouteCount += 1
      excessTierSteps += delta
    }
  }

  const caseCount = cases.length
  return {
    caseCount,
    semanticRoutedCount,
    semanticCoverage: safeRate(
      semanticRoutedCount,
      caseCount,
    ),
    fallbackUsedCount,
    semanticOverrideCount,
    exactCount,
    exactRate: safeRate(exactCount, caseCount),
    falseDowngradeCount,
    falseDowngradeRate: safeRate(
      falseDowngradeCount,
      caseCount,
    ),
    downgradeSeveritySteps,
    overrouteCount,
    excessTierSteps,
    meanExcessTier: safeRate(excessTierSteps, caseCount),
  }
}
